import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { CategoryClient, CategoryDto, CategoryType } from '../../core/services/service-clients';
import { firstValueFrom } from 'rxjs';

interface CategoryState {
  categories: CategoryDto[];
  loading: boolean;
  initialized: boolean;
}

const initialState: CategoryState = {
  categories: [],
  loading: false,
  initialized: false
};

export const categoryStore = signalStore(
  withState<CategoryState>(initialState),

  withComputed(({ categories }) => ({
    menuCategories: computed(() =>
      categories()
        .filter((c) => c.type === CategoryType.Menu)
        .sort((a, b) => a.orderIndex - b.orderIndex),
    ),
    menuTopLevelCategories: computed(() =>
      categories()
        .filter((c) => c.type === CategoryType.Menu && c.parentCategoryId == null)
        .sort((a, b) => a.orderIndex - b.orderIndex),
    ),
    menuHierarchy: computed(() => {
      const menu = categories().filter((c) => c.type === CategoryType.Menu);
      const byOrder = (a: CategoryDto, b: CategoryDto) => a.orderIndex - b.orderIndex;
      return menu
        .filter((c) => c.parentCategoryId == null)
        .sort(byOrder)
        .map((parent) => ({
          parent,
          children: menu.filter((c) => c.parentCategoryId === parent.id).sort(byOrder),
        }));
    }),
  })),

  // Flat dropdown options: each top-level category then its subcategories ("Parent → Sub").
  withComputed(({ menuHierarchy }) => ({
    menuCategoryOptions: computed(() => {
      const options: { id: number; label: string }[] = [];
      for (const { parent, children } of menuHierarchy()) {
        options.push({ id: parent.id, label: parent.name });
        for (const child of children) {
          options.push({ id: child.id, label: `${parent.name} → ${child.name}` });
        }
      }
      return options;
    }),
  })),

  withMethods((store) => {
    const categoryClient = inject(CategoryClient);

    return {
      async loadCategories() {
        // Check if categories are already loaded
        if (store.initialized()) {
          return true;
        }

        patchState(store, { loading: true });
        try {
          const categories = await firstValueFrom(categoryClient.get());
          patchState(store, {
            categories,
            loading: false,
            initialized: true // Mark as initialized after successful load
          });
          return true;
        } catch {
          patchState(store, { loading: false });
          return false;
        }
      },

      async addCategory(category: CategoryDto) {
        patchState(store, { loading: true });
        try {
          const categories = await firstValueFrom(categoryClient.post(category));
          patchState(store, {
            categories,
            loading: false,
            initialized: true
          });
          return true;
        } catch {
          patchState(store, { loading: false });
          return false;
        }
      },

      async updateCategory(category: CategoryDto) {
        patchState(store, { loading: true });
        try {
          const categories = await firstValueFrom(categoryClient.put(category));
          patchState(store, {
            categories,
            loading: false,
            initialized: true
          });
          return true;
        } catch {
          patchState(store, { loading: false });
          return false;
        }
      },

      async deleteCategory(categoryId: number) {
        patchState(store, { loading: true });
        try {
          const categories = await firstValueFrom(categoryClient.delete(categoryId));
          patchState(store, {
            categories,
            loading: false,
            initialized: true
          });
          return true;
        } catch {
          patchState(store, { loading: false });
          return false;
        }
      },

      // Force reload categories if needed
      async forceLoadCategories() {
        patchState(store, { loading: true });
        try {
          const categories = await firstValueFrom(categoryClient.get());
          patchState(store, {
            categories,
            loading: false,
            initialized: true
          });
          return true;
        } catch {
          patchState(store, { loading: false });
          return false;
        }
      },

      async reorderCategories(orderedIds: number[]) {
        patchState(store, { loading: true });
        try {
          const categories = await firstValueFrom(categoryClient.reorder(orderedIds));
          patchState(store, {
            categories,
            loading: false,
            initialized: true
          });
          return true;
        } catch {
          patchState(store, { loading: false });
          return false;
        }
      },

      getCategoryById(id: number) {
        return computed(() => store.categories().find(category => category.id === id));
      }
    };
  })
);
